import gql from "graphql-tag";
import {useCallback} from "react";

import {useMutation} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {
    setTeacherCourses,
    setTeacherCourses_setTeacherCourses as SetTeacherCoursesResult,
    setTeacherCoursesVariables,
} from "./__graphql-types__/setTeacherCourses.ts";
import type {
    setTeacherGradeLevels,
    setTeacherGradeLevels_setTeacherGradeLevels as SetTeacherGradeLevelsResult,
    setTeacherGradeLevelsVariables,
} from "./__graphql-types__/setTeacherGradeLevels.ts";

export const SET_TEACHER_GRADE_LEVELS_MUTATION: GqlOperation<
    setTeacherGradeLevels,
    setTeacherGradeLevelsVariables
> = gqlOp(gql`
    mutation setTeacherGradeLevels(
        $kaid: String!
        $gradeLevels: [TeacherGradeLevel!]!
    ) {
        setTeacherGradeLevels(kaid: $kaid, teacherGradeLevels: $gradeLevels) {
            error {
                code
            }
        }
    }
`);

export const SET_TEACHER_COURSES_MUTATION: GqlOperation<
    setTeacherCourses,
    setTeacherCoursesVariables
> = gqlOp(gql`
    mutation setTeacherCourses($kaid: String!, $courses: [TeacherCourse!]!) {
        setTeacherCourses(kaid: $kaid, teacherCourses: $courses) {
            error {
                code
            }
        }
    }
`);

export const useSetTeacherGradeLevels = (): ((
    variables: setTeacherGradeLevelsVariables,
) => Promise<SetTeacherGradeLevelsResult>) => {
    const [mutate] = useMutation(SET_TEACHER_GRADE_LEVELS_MUTATION);

    return useCallback(
        async (
            variables: setTeacherGradeLevelsVariables,
        ): Promise<SetTeacherGradeLevelsResult> => {
            try {
                const result = await mutate(variables);
                if (result.status === "error") {
                    throw new KAError(
                        "Failed to set teacher grade levels",
                        Errors.Service,
                        {
                            cause: result.error,
                        },
                    );
                }
                if (result.status === "success") {
                    const response = result.data?.setTeacherGradeLevels;
                    if (!response) {
                        throw new KAError(
                            "No response from setTeacherGradeLevels",
                            Errors.Service,
                        );
                    }
                    if (response.error) {
                        throw new KAError(
                            "Failed to set teacher grade levels",
                            Errors.Service,
                            {
                                cause: response.error.code,
                            },
                        );
                    }
                    return response;
                }
                throw new KAError(
                    "Unexpected mutation result status",
                    Errors.Service,
                );
            } catch (error: unknown) {
                if (error instanceof KAError) {
                    throw error;
                }
                throw new KAError(
                    "Failed to set teacher grade levels",
                    Errors.Internal,
                );
            }
        },
        [mutate],
    );
};

export const useSetTeacherCourses = (): ((
    variables: setTeacherCoursesVariables,
) => Promise<SetTeacherCoursesResult>) => {
    const [mutate] = useMutation(SET_TEACHER_COURSES_MUTATION);

    return useCallback(
        async (
            variables: setTeacherCoursesVariables,
        ): Promise<SetTeacherCoursesResult> => {
            try {
                const result = await mutate(variables);
                if (result.status === "error") {
                    throw new KAError(
                        "Failed to set teacher courses",
                        Errors.Service,
                        {
                            cause: result.error,
                        },
                    );
                }
                if (result.status === "success") {
                    const response = result.data?.setTeacherCourses;
                    if (!response) {
                        throw new KAError(
                            "No response from setTeacherCourses",
                            Errors.Service,
                        );
                    }
                    if (response.error) {
                        throw new KAError(
                            "Failed to set teacher courses",
                            Errors.Service,
                            {
                                cause: response.error.code,
                            },
                        );
                    }
                    return response;
                }
                throw new KAError(
                    "Unexpected mutation result status",
                    Errors.Service,
                );
            } catch (error: unknown) {
                if (error instanceof KAError) {
                    throw error;
                }
                throw new KAError(
                    "Failed to set teacher courses",
                    Errors.Internal,
                );
            }
        },
        [mutate],
    );
};
